import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { REASON_TAX_CLASS_DATA_TYPE, REASON_TAX_CLASSES, ReasonTaxClass } from "@/constants/ReasonTaxClasses";
import { upsertAddInData } from "@/utilities/addInData/upsertAddInData";
import { decompressData } from "@/utilities/compression";

export const REASON_TAX_CLASSES_QUERY_KEY = "logbookAuditReasonTaxClasses";

/** Per-database overrides of the default tax class assignment, keyed by normalized reason. */
export interface ReasonTaxClassSettings {
  overrides: Record<string, ReasonTaxClass>;
}

interface RawSettingsDetails {
  dataType?: string;
  overrides?: Record<string, string>;
}

function isReasonTaxClass(value: string): value is ReasonTaxClass {
  return (REASON_TAX_CLASSES as readonly string[]).includes(value);
}

/**
 * Reads the single per-database AddInData record that stores the user-changed tax classes.
 * `dataType` stays a plain field so Geotab's `whereClause` can find it; the overrides are read
 * from the compressed payload, or from the plain `overrides` field of records written before.
 */
export function useReasonTaxClassSettings() {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery<ReasonTaxClassSettings>({
    queryKey: [REASON_TAX_CLASSES_QUERY_KEY, activeSessionId],
    queryFn: async ({ signal }) => {
      const records = await geotabClient.get(
        "AddInData",
        {
          addInId: AddInIds.geotabAddin,
          whereClause: `dataType = "${REASON_TAX_CLASS_DATA_TYPE}"`,
        },
        10,
        signal,
      );
      // Several rows can only come from a race of two first writes; the latest one wins.
      const newest = records.reduce<(typeof records)[number] | undefined>(
        (best, record) => (!best || updatedAtOf(record) > updatedAtOf(best) ? record : best),
        undefined,
      );
      if (!newest) return { overrides: {} };
      const details = decompressData<RawSettingsDetails>(newest.details);
      const overrides: Record<string, ReasonTaxClass> = {};
      for (const [reason, taxClass] of Object.entries(details?.overrides ?? {})) {
        if (isReasonTaxClass(taxClass)) overrides[reason] = taxClass;
      }
      return { overrides };
    },
  });
}

const updatedAtOf = (record: { details: unknown }) =>
  (record.details as { updatedAt?: number } | undefined)?.updatedAt ?? 0;

export function useSaveReasonTaxClassSettings() {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();
  const queryKey = [REASON_TAX_CLASSES_QUERY_KEY, activeSessionId];

  return useMutation({
    mutationFn: async (settings: ReasonTaxClassSettings): Promise<ReasonTaxClassSettings> => {
      await upsertAddInData({
        geotabClient,
        addInId: AddInIds.geotabAddin,
        lookup: { field: "dataType", value: REASON_TAX_CLASS_DATA_TYPE },
        plain: { dataType: REASON_TAX_CLASS_DATA_TYPE, updatedAt: Date.now() },
        payload: { overrides: settings.overrides },
      });
      return settings;
    },
    onMutate: async settings => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ReasonTaxClassSettings>(queryKey);
      queryClient.setQueryData<ReasonTaxClassSettings>(queryKey, settings);
      return { previous };
    },
    // The Set response confirms the state, so the cache is authoritative afterwards. A refetch
    // right after the Set can still read the old record from a Geotab replica and would undo
    // the change on screen; only a failed save goes back to the server.
    onSuccess: saved => queryClient.setQueryData<ReasonTaxClassSettings>(queryKey, saved),
    onError: (_error, _settings, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
